import { Window } from '@execonline-inc/browser-window.private';
import { array, field, string } from 'jsonous';
import { just, Maybe, nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError, callApi, deleteToApi } from '../../Appy';
import { findLinkT, MissingLinkError } from '../../LinkyLinky';
import { Link } from '../../Resource/Types';
import {
  connectionResourceDecoder,
  ssoConfigurationsResourceDecoder,
  testSignInLaunchDecoder,
} from './Decoders';
import { ConnectionResource, SsoConfigurationsResource, SsoEnvironment, SsoVendor } from './Types';

export type SsoApiError = AppyError | MissingLinkError;

export const interpolateLink =
  (placeholder: string, value: string | number) =>
  (link: Link): Link => ({
    ...link,
    href: link.href.replace(placeholder, `${value}`),
  });

export interface NewConnection {
  displayName: string;
  vendor: SsoVendor;
  environment: SsoEnvironment;
}

export const createConnection = (
  links: ReadonlyArray<Link>,
  newConnection: NewConnection,
): Task<SsoApiError, ConnectionResource> =>
  Task.succeed<SsoApiError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('create'))
    .andThen(
      callApi(connectionResourceDecoder, {
        sso_configuration: {
          display_name: newConnection.displayName,
          vendor: newConnection.vendor,
          environment: newConnection.environment,
        },
      }),
    );

export const submitMetadata = (
  links: ReadonlyArray<Link>,
  metadataUrl: string,
): Task<SsoApiError, ConnectionResource> =>
  Task.succeed<SsoApiError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('submit-metadata'))
    .andThen(
      callApi(connectionResourceDecoder, { sso_configuration: { metadata_url: metadataUrl } }),
    );

export const assignDomain = (
  links: ReadonlyArray<Link>,
  loginDomainId: number,
  ssoConnectionId: Maybe<number>,
): Task<SsoApiError, SsoConfigurationsResource> =>
  Task.succeed<SsoApiError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('assign-domain'))
    .map(interpolateLink('{login_domain_id}', loginDomainId))
    .andThen(
      callApi(ssoConfigurationsResourceDecoder, {
        sso_domain_assignment: {
          sso_connection_id: ssoConnectionId.map<number | null>((id) => id).getOrElseValue(null),
        },
      }),
    );

export const updateAttributeMapping = (
  links: ReadonlyArray<Link>,
  mapping: { [field: string]: string },
): Task<SsoApiError, ConnectionResource> =>
  Task.succeed<SsoApiError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('update-attribute-mapping'))
    .andThen(
      callApi(connectionResourceDecoder, { sso_configuration: { attribute_mapping: mapping } }),
    );

export const deleteConnection = (links: ReadonlyArray<Link>): Task<SsoApiError, string> =>
  Task.succeed<SsoApiError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('delete'))
    .andThen(deleteToApi);

// Launches a forced-provider test sign-in: mint the short-lived token, then
// open the bounce page on the platform host in a new tab; that page performs
// the same-origin POST to the OmniAuth request path (Rails' forgery origin
// check rejects cross-origin posts from the Admin Studio host).
//
// The launch host comes from P3_URL, which is an execonline.com subdomain in
// every environment (platform.local.execonline.com locally) and so clears
// Window's trusted-host allowlist. openWindow is fire-and-forget by
// necessity: noopener makes window.open return null even on success, so a
// Task-returning open could not tell a blocked popup from a launched one.
export const launchTestSignIn = (links: ReadonlyArray<Link>): Task<SsoApiError, void> =>
  Task.succeed<SsoApiError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('test-sign-in'))
    .andThen(callApi(testSignInLaunchDecoder, {}))
    .map(({ actionUrl }) =>
      Window.openWindow(actionUrl, '_blank', { noopener: just(true), noreferrer: nothing() }),
    );

const serverErrorsDecoder = field('errors', array(string));

// Server validation errors ({ errors: [...] }) arrive as the bad-status body;
// they are rendered via NotTranslated since the copy originates server-side.
export const serverErrorMessage = (error: SsoApiError): Maybe<string> => {
  switch (error.kind) {
    case 'bad-status':
      return serverErrorsDecoder
        .decodeJson(`${error.response.body}`)
        .map((errors) => errors.join(' '))
        .cata<Maybe<string>>({
          Ok: (message) => just(message),
          Err: () => nothing(),
        });
    case 'bad-payload':
    case 'bad-url':
    case 'missing-application-id':
    case 'missing-api-compatibility':
    case 'network-error':
    case 'timeout':
    case 'missing-link-error':
      return nothing();
  }
};
